import type { IconName } from "@sagot/ui";
import type { MessageView, TimelineView } from "./inbox-model";

/** Timeline events that render as a divider pill (messages already show replies and notes). */
const EVENT_SPECS: Readonly<Record<string, { icon: IconName; tone: "danger" | "primary" | "muted" | "success" }>> = {
  handoff_requested: { icon: "headset", tone: "danger" },
  escalated: { icon: "alert-triangle", tone: "danger" },
  assigned: { icon: "user-plus", tone: "muted" },
  status_changed: { icon: "check-circle", tone: "muted" },
  takeover: { icon: "headset", tone: "primary" },
  released: { icon: "bot", tone: "muted" },
  returned_to_queue: { icon: "users", tone: "muted" },
  contact_captured: { icon: "mail", tone: "success" },
  added_to_knowledge: { icon: "database", tone: "success" },
  rated: { icon: "thumbs-up", tone: "success" },
  snoozed: { icon: "clock", tone: "muted" },
  unsnoozed: { icon: "bell", tone: "muted" },
};

export type EventTone = (typeof EVENT_SPECS)[string]["tone"];

export type TimelineEntry =
  | { kind: "message"; id: string; at: string; message: MessageView }
  | { kind: "event"; id: string; at: string; event: TimelineView; icon: IconName; tone: EventTone }
  | { kind: "day"; id: string; at: string };

/** Same-timestamp ordering: the visitor asks before the assistant answers, staff after both. */
const ROLE_RANK: Readonly<Record<string, number>> = { user: 0, system: 1, assistant: 2, staff: 3, note: 4 };
const EVENT_RANK = 5;

function rankOf(entry: Exclude<TimelineEntry, { kind: "day" }>): number {
  return entry.kind === "message" ? (ROLE_RANK[entry.message.role] ?? 1) : EVENT_RANK;
}

export function isRenderedEvent(event: TimelineView): boolean {
  return event.type in EVENT_SPECS;
}

/**
 * Messages + timeline events in one chronological list with a day separator before each new
 * calendar day (`dayKey` maps an ISO time to the workspace-TZ date).
 */
export function buildTimeline(messages: readonly MessageView[], events: readonly TimelineView[], dayKey: (iso: string) => string): TimelineEntry[] {
  const items: Exclude<TimelineEntry, { kind: "day" }>[] = [
    ...messages.map((message) => ({ kind: "message" as const, id: message.id, at: message.created_at, message })),
    ...events.filter(isRenderedEvent).map((event) => {
      const spec = EVENT_SPECS[event.type] ?? { icon: "info" as const, tone: "muted" as const };
      return { kind: "event" as const, id: event.id, at: event.created_at, event, icon: spec.icon, tone: spec.tone };
    }),
  ];
  const sorted = items
    .map((entry, index) => ({ entry, index, time: new Date(entry.at).getTime() }))
    .sort((a, b) => a.time - b.time || rankOf(a.entry) - rankOf(b.entry) || a.index - b.index)
    .map((x) => x.entry);
  const out: TimelineEntry[] = [];
  let lastDay = "";
  for (const entry of sorted) {
    const day = dayKey(entry.at);
    if (day !== lastDay) {
      out.push({ kind: "day", id: `day-${day}`, at: entry.at });
      lastDay = day;
    }
    out.push(entry);
  }
  return out;
}

/** Last staff reply and the visitor question before it (Add to Knowledge prefill). */
export function knowledgeCandidate(messages: readonly MessageView[]): { message: MessageView; question: string } | null {
  const ordered = [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const staffIndex = ordered.findLastIndex((m) => m.role === "staff");
  const index = staffIndex >= 0 ? staffIndex : ordered.findLastIndex((m) => m.role === "assistant");
  const message = ordered[index];
  if (!message) return null;
  const question = ordered
    .slice(0, index)
    .reverse()
    .find((m) => m.role === "user");
  return { message, question: question?.content ?? "" };
}
